<template>
  <div class="sc-page">
    <!-- Not configured: explain rather than render empty charts. -->
    <section v-if="status && !status.configured" class="sc-setup">
      <span class="eyebrow">Not connected</span>
      <h5>Google Search Console isn't configured yet</h5>
      <p>
        Set <code>SEARCH_CONSOLE_SITE_URL</code>, <code>SEARCH_CONSOLE_CLIENT_SECRET</code> and
        <code>SEARCH_CONSOLE_REFRESH_TOKEN</code> in the backend <code>.env</code>. Mint the
        refresh token by running <code>node scripts/searchConsoleAuth.js</code> and signing in as
        an account that already has access to the property.
        Full walkthrough in <code>docs/SEARCH_CONSOLE_SETUP.md</code>.
      </p>
    </section>

    <div v-if="error" class="alert">{{ error }}</div>

    <!-- Controls -->
    <div class="sc-controls">
      <div class="sc-ranges">
        <button
          v-for="option in rangeOptions"
          :key="option.days"
          class="sc-range"
          :class="{ active: days === option.days }"
          type="button"
          @click="setRange(option.days)"
        >{{ option.label }}</button>
      </div>
      <div class="sc-control-actions">
        <span v-if="status && status.latest_date" class="sc-freshness">
          Data through <strong>{{ status.latest_date }}</strong>
          <template v-if="status.auth_mode"> · auth <strong>{{ authModeLabel }}</strong></template>
        </span>
        <button class="btn-refresh" type="button" :disabled="loading" @click="fetchAll()">
          {{ loading ? 'Loading...' : 'Refresh' }}
        </button>
        <button
          v-if="status && status.configured"
          class="btn-refresh"
          type="button"
          :disabled="syncing"
          @click="syncNow()"
        >{{ syncing ? 'Syncing...' : 'Sync from Google' }}</button>
      </div>
    </div>
    <p v-if="syncMessage" class="sc-sync-note">{{ syncMessage }}</p>

    <!-- KPI row: headline numbers are stat tiles, not one-bar charts. -->
    <div class="kpi-row sc-kpis">
      <div v-for="card in kpiCards" :key="card.key" class="kpi-card">
        <span class="kpi-label">{{ card.label }}</span>
        <span class="kpi-value">{{ card.value }}</span>
        <span class="kpi-note">{{ card.note }}</span>
      </div>
    </div>

    <!--
      Clicks and impressions are deliberately SEPARATE charts sharing one x-axis
      (small multiples). Impressions run ~50x clicks, so plotting both on one
      pair of axes would either flatten clicks to zero or need a second y-scale.
    -->
    <div class="sc-charts">
      <figure v-for="chart in trendCharts" :key="chart.key" class="sc-chart-card">
        <figcaption>
          <div>
            <span class="sc-chart-title">{{ chart.title }}</span>
            <span class="sc-chart-sub">{{ chart.subtitle }}</span>
          </div>
          <strong class="sc-chart-total" :style="{ color: chart.color }">{{ chart.totalLabel }}</strong>
        </figcaption>

        <div class="sc-plot" @mouseleave="hover = null">
          <svg
            :viewBox="`0 0 ${plot.width} ${plot.height}`"
            preserveAspectRatio="none"
            class="sc-svg"
            role="img"
            :aria-label="chart.title"
          >
            <!-- recessive gridlines -->
            <line
              v-for="(y, i) in chart.gridY"
              :key="'g' + i"
              class="sc-grid"
              :x1="plot.padLeft" :x2="plot.width - plot.padRight"
              :y1="y" :y2="y"
            />
            <path v-if="chart.area" class="sc-area" :d="chart.area" :fill="chart.color" />
            <path
              v-if="chart.line"
              class="sc-line"
              :d="chart.line"
              :stroke="chart.color"
              fill="none"
            />
            <!-- hover crosshair + focused point -->
            <template v-if="hoverPoint(chart)">
              <line
                class="sc-crosshair"
                :x1="hoverPoint(chart).x" :x2="hoverPoint(chart).x"
                :y1="plot.padTop" :y2="plot.height - plot.padBottom"
              />
              <circle
                class="sc-dot"
                :cx="hoverPoint(chart).x"
                :cy="hoverPoint(chart).y"
                r="4.5"
                :fill="chart.color"
              />
            </template>
            <!-- transparent hit layer, wider than the marks -->
            <rect
              class="sc-hit"
              :x="plot.padLeft"
              :y="0"
              :width="Math.max(1, plot.width - plot.padLeft - plot.padRight)"
              :height="plot.height"
              @mousemove="onPlotMove($event)"
            />
          </svg>

          <div class="sc-axis-y">
            <span v-for="(label, i) in chart.yLabels" :key="'y' + i">{{ label }}</span>
          </div>

          <div v-if="hover && hoverRow" class="sc-tooltip" :style="tooltipStyle">
            <span class="sc-tip-date">{{ hoverRow.date }}</span>
            <span class="sc-tip-value">
              <i :style="{ background: chart.color }"></i>
              {{ chart.title }}: <strong>{{ chart.format(hoverRow[chart.field]) }}</strong>
            </span>
          </div>
        </div>

        <div class="sc-axis-x">
          <span>{{ firstDate }}</span>
          <span>{{ lastDate }}</span>
        </div>
      </figure>
    </div>

    <div v-if="!daily.length && !loading" class="empty sc-empty">
      No Search Console data for this range yet. Run a sync, and remember Google finalizes
      data on a 2–3 day delay.
    </div>

    <!-- Full-dataset diagnosis: calculated by the backend before table limits. -->
    <section v-if="diagnostics && diagnosticScopeData" class="sc-analysis-section">
      <div class="sc-section-head">
        <div>
          <span class="eyebrow">Ranking diagnosis</span>
          <h5>Where impressions are actually ranking</h5>
          <p>{{ rankingSummary }}</p>
        </div>
        <div class="sc-segmented" aria-label="Ranking scope">
          <button type="button" :class="{ active: diagnosticScope === 'parts' }" @click="diagnosticScope = 'parts'">Part pages</button>
          <button type="button" :class="{ active: diagnosticScope === 'all' }" @click="diagnosticScope = 'all'">All pages</button>
        </div>
      </div>

      <div class="sc-diagnostic-grid">
        <div class="sc-diagnostic-card">
          <div class="insight-head">
            <span>CTR by position bucket</span>
            <strong>{{ diagnostics.host || 'All hosts' }}</strong>
          </div>
          <div class="sc-rank-table" role="table" aria-label="CTR by average position bucket">
            <div class="sc-rank-head" role="row">
              <span>Position</span><span>Pages</span><span>Clicks</span><span>Impr.</span><span>CTR</span>
            </div>
            <div v-for="bucket in positionBuckets" :key="bucket.key" class="sc-rank-row" role="row">
              <strong>{{ bucket.label }}</strong>
              <span>{{ fmtNum(bucket.pages) }}</span>
              <span>{{ fmtNum(bucket.clicks) }}</span>
              <span>{{ fmtNum(bucket.impressions) }}</span>
              <span>{{ fmtPct(bucket.ctr) }}</span>
            </div>
          </div>
          <p class="sc-method-note">{{ diagnostics.methodology }}</p>
        </div>

        <div class="sc-diagnostic-card">
          <div class="insight-head">
            <span>Device performance</span>
            <strong>Ranking + response</strong>
          </div>
          <div class="sc-device-table">
            <div class="sc-device-head">
              <span>Device</span><span>Clicks</span><span>Impr.</span><span>CTR</span><span>Pos.</span>
            </div>
            <div v-for="row in deviceRows" :key="row.device" class="sc-device-row">
              <strong>{{ row.label }}</strong>
              <span>{{ fmtNum(row.clicks) }}</span>
              <span>{{ fmtNum(row.impressions) }}</span>
              <span>{{ fmtPct(row.ctr) }}</span>
              <span>{{ fmtPos(row.position) }}</span>
            </div>
            <div v-if="!deviceRows.length" class="sc-list-empty">No device data.</div>
          </div>
          <p v-if="diagnostics.query_coverage" class="sc-method-note">
            Visible query rows cover {{ fmtPct(diagnostics.query_coverage.share) }} of total impressions;
            privacy-hidden queries remain in headline totals.
          </p>
        </div>
      </div>
    </section>

    <!-- Device split + index coverage -->
    <div class="audience-insights sc-insights">
      <div class="insight-card">
        <div class="insight-head">
          <span>Index coverage</span>
          <strong>{{ indexSummary.checked ? indexSummary.indexedPercent + '% indexed' : '—' }}</strong>
        </div>
        <div class="sc-coverage">
          <!-- status colors always ship with a label, never color alone -->
          <div class="sc-coverage-row">
            <span class="sc-status"><i class="ok"></i> Indexed</span>
            <strong>{{ fmtNum(indexSummary.indexed) }}</strong>
          </div>
          <div class="sc-coverage-row">
            <span class="sc-status"><i class="bad"></i> Not indexed</span>
            <strong>{{ fmtNum(indexSummary.notIndexed) }}</strong>
          </div>
          <div class="sc-meter">
            <i :style="{ width: indexSummary.indexedPercent + '%' }"></i>
          </div>
          <span class="sc-coverage-note">
            <template v-if="!indexSummary.checked">Run the index check job to populate this.</template>
            <template v-else>
              {{ fmtNum(indexSummary.checked) }} of {{ fmtNum(indexSummary.catalog) }} part URLs checked<template
                v-if="indexSummary.partial"> — {{ indexSummary.scanPercent }}% of the catalog scanned so far</template>.
              Search Console's own page count spans every subdomain and non-part page, so it always reads higher.
            </template>
          </span>
        </div>
      </div>

      <div class="insight-card">
        <div class="insight-head">
          <span>Coverage states</span>
          <strong>{{ coverage.length ? coverage[0].coverage_state : '—' }}</strong>
        </div>
        <div class="insight-bars">
          <div v-for="item in coverageChart" :key="item.label" class="insight-bar">
            <span :title="item.label">{{ item.label }}</span>
            <div><i :style="{ width: item.percent + '%', background: item.color }"></i></div>
            <strong>{{ fmtNum(item.count) }}</strong>
          </div>
          <div v-if="!coverageChart.length" class="sc-list-empty">No index checks recorded.</div>
        </div>
      </div>

      <div class="insight-card">
        <div class="insight-head">
          <span>Search interpretation</span>
          <strong>{{ diagnosticScope === 'parts' ? 'Part pages' : 'All pages' }}</strong>
        </div>
        <div class="sc-interpretation">
          <strong>{{ rankingHeadline }}</strong>
          <span>Use the position buckets above before treating blended CTR as a snippet-quality score.</span>
        </div>
      </div>
    </div>

    <!-- AI Overview / AI Mode visibility. These are impressions, not clicks. -->
    <section class="sc-ai-section">
      <div class="sc-section-head">
        <div>
          <span class="eyebrow">Generative AI visibility</span>
          <h5>AI Overviews and AI Mode</h5>
          <p>AI impressions are already included in Web impressions. Google does not provide AI clicks, CTR, position, or queries in this report.</p>
        </div>
        <div class="sc-import-actions">
          <input ref="genAiInput" class="sc-file-input" type="file" accept=".csv,text/csv" multiple webkitdirectory directory @change="importGenAiReport">
          <button class="btn-refresh" type="button" :disabled="importingGenAi" @click="openGenAiImport">
            {{ importingGenAi ? 'Importing...' : 'Import AI report folder' }}
          </button>
        </div>
      </div>
      <p v-if="genAiMessage" class="sc-sync-note">{{ genAiMessage }}</p>

      <template v-if="genAi && genAi.available">
        <div class="sc-ai-kpis">
          <div class="sc-ai-kpi"><span>AI impressions</span><strong>{{ fmtNum(genAi.totals.impressions) }}</strong><small>in the selected date range</small></div>
          <div class="sc-ai-kpi"><span>Share of Web impressions</span><strong>{{ fmtPct(genAi.totals.share) }}</strong><small>overlapping exposure, not additional traffic</small></div>
          <div class="sc-ai-kpi"><span>AI-visible pages</span><strong>{{ fmtNum(genAi.totals.pages) }}</strong><small>{{ genAiSnapshotLabel }}</small></div>
          <div class="sc-ai-kpi"><span>Latest AI activity</span><strong>{{ genAiLastDate || '—' }}</strong><small>latest imported daily row</small></div>
        </div>

        <div v-if="genAiBars.length" class="sc-ai-trend" aria-label="Daily generative AI impressions">
          <div class="sc-ai-trend-head"><span>Daily AI impressions</span><strong>{{ genAiFirstDate }} – {{ genAiLastDate }}</strong></div>
          <div class="sc-ai-bars">
            <i v-for="bar in genAiBars" :key="bar.date" :style="{ height: bar.height + '%' }" :title="bar.date + ': ' + bar.impressions + ' impressions'"></i>
          </div>
        </div>

        <div class="sc-ai-grid">
          <div class="sc-table-wrap">
            <table class="sc-table">
              <thead><tr><th>Top AI-visible page</th><th class="num">AI impr.</th><th class="num">Web impr.</th><th class="num">AI share</th><th class="num">Web pos.</th></tr></thead>
              <tbody>
                <tr v-for="page in genAiTopPages" :key="page.page">
                  <td><a :href="page.page" target="_blank" rel="noopener">{{ page.page_path || page.page }}</a></td>
                  <td class="num">{{ fmtNum(page.ai_impressions) }}</td>
                  <td class="num">{{ fmtNum(page.web_impressions) }}</td>
                  <td class="num">{{ fmtPct(page.ai_share) }}</td>
                  <td class="num">{{ fmtPos(page.web_position) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="sc-ai-breakdowns">
            <div class="sc-diagnostic-card">
              <div class="insight-head"><span>Content types</span><strong>AI impressions</strong></div>
              <div v-for="row in genAi.content_types" :key="row.type" class="sc-breakdown-row"><span>{{ prettyType(row.type) }}</span><strong>{{ fmtNum(row.impressions) }}</strong></div>
            </div>
            <div class="sc-diagnostic-card">
              <div class="insight-head"><span>Devices</span><strong>Latest export</strong></div>
              <div v-for="row in genAi.devices" :key="row.value" class="sc-breakdown-row"><span>{{ prettyDevice(row.value) }}</span><strong>{{ fmtNum(row.impressions) }}</strong></div>
            </div>
          </div>
        </div>
      </template>
      <div v-else class="empty sc-empty sc-ai-empty">
        Import the outer folder exported from Search Console's Generative AI report. It should contain Chart.csv, Pages.csv, Countries.csv, Devices.csv, and Filters.csv.
      </div>
    </section>

    <section v-if="nearWins.length" class="sc-opportunity-section">
      <div class="sc-section-head">
        <div><span class="eyebrow">SEO work queue</span><h5>Near-win part pages</h5><p>Pages averaging positions 4–20, ordered by impressions. Improve the existing canonical page first.</p></div>
      </div>
      <div class="sc-table-wrap">
        <table class="sc-table">
          <thead><tr><th>Part</th><th>Type</th><th>Opportunity</th><th class="num">Clicks</th><th class="num">Impressions</th><th class="num">CTR</th><th class="num">Avg pos</th></tr></thead>
          <tbody>
            <tr v-for="part in nearWins" :key="part.part_type + ':' + part.part_id">
              <td><a :href="partUrl(part)" target="_blank" rel="noopener">{{ part.model || part.part_slug }}</a></td>
              <td class="muted">{{ part.part_type }}</td>
              <td><span class="sc-pill opportunity">{{ opportunityLabel(part) }}</span></td>
              <td class="num">{{ fmtNum(part.clicks) }}</td><td class="num">{{ fmtNum(part.impressions) }}</td><td class="num">{{ fmtPct(part.ctr) }}</td><td class="num">{{ fmtPos(part.position) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Detail tables -->
    <div class="tabs-row sc-tabs-row">
      <div class="tabs">
        <button class="tab" :class="{ active: table === 'parts' }" @click="table = 'parts'">
          Parts <span class="tab-count">{{ parts.length }}</span>
        </button>
        <button class="tab" :class="{ active: table === 'pages' }" @click="table = 'pages'">
          Pages <span class="tab-count">{{ pages.length }}</span>
        </button>
        <button class="tab" :class="{ active: table === 'queries' }" @click="table = 'queries'">
          Queries <span class="tab-count">{{ queries.length }}</span>
        </button>
      </div>
      <div class="filters" v-if="table === 'parts' || table === 'pages'">
        <!-- A domain property reports every subdomain, so prod/beta/vr need separating. -->
        <select v-if="hosts.length > 1" class="hfilter" v-model="host" @change="onHostChange()">
          <option value="">All hosts</option>
          <option v-for="item in hosts" :key="item.host" :value="item.host">
            {{ item.host }} ({{ fmtNum(item.impressions) }})
          </option>
        </select>
        <select v-if="table === 'parts'" class="hfilter" v-model="partType" @change="fetchParts()">
          <option value="">All part types</option>
          <option v-for="type in partTypes" :key="type" :value="type">{{ type }}</option>
        </select>
      </div>
    </div>

    <!-- Parts: the join that Search Console itself can't do. -->
    <div v-show="table === 'parts'" class="sc-table-wrap">
      <table class="sc-table">
        <thead>
          <tr>
            <th>Part</th>
            <th>Type</th>
            <th class="num">Clicks</th>
            <th class="num">Impressions</th>
            <th class="num">CTR</th>
            <th class="num">Avg pos</th>
            <th>Indexed</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="part in parts" :key="part.part_type + ':' + part.part_id">
            <td>
              <a :href="partUrl(part)" target="_blank" rel="noopener" :title="part.page">
                {{ part.model || part.part_slug }}
              </a>
              <span
                v-if="part.host_count > 1"
                class="sc-pill unknown"
                :title="'Summed across ' + part.host_count + ' hosts (e.g. production + beta)'"
              >{{ part.host_count }} hosts</span>
            </td>
            <td class="muted">{{ part.part_type }}</td>
            <td class="num">{{ fmtNum(part.clicks) }}</td>
            <td class="num">{{ fmtNum(part.impressions) }}</td>
            <td class="num">{{ fmtPct(part.ctr) }}</td>
            <td class="num">{{ fmtPos(part.position) }}</td>
            <td>
              <span v-if="part.is_indexed === true" class="sc-pill ok">Indexed</span>
              <span v-else-if="part.is_indexed === false" class="sc-pill bad" :title="part.coverage_state">Not indexed</span>
              <span v-else class="sc-pill unknown">Unchecked</span>
            </td>
          </tr>
          <tr v-if="!parts.length"><td colspan="7" class="empty">No part pages with search data in this range.</td></tr>
        </tbody>
      </table>
    </div>

    <div v-show="table === 'pages'" class="sc-table-wrap">
      <table class="sc-table">
        <thead>
          <tr>
            <th>Page</th>
            <th>Host</th>
            <th>Kind</th>
            <th class="num">Clicks</th>
            <th class="num">Impressions</th>
            <th class="num">CTR</th>
            <th class="num">Avg pos</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="page in pages" :key="page.page">
            <td>
              <a :href="page.page" target="_blank" rel="noopener" :title="page.page">
                {{ page.page_path || page.page }}
              </a>
            </td>
            <td class="muted">{{ page.host || '—' }}</td>
            <td class="muted">{{ page.page_kind }}</td>
            <td class="num">{{ fmtNum(page.clicks) }}</td>
            <td class="num">{{ fmtNum(page.impressions) }}</td>
            <td class="num">{{ fmtPct(page.ctr) }}</td>
            <td class="num">{{ fmtPos(page.position) }}</td>
          </tr>
          <tr v-if="!pages.length"><td colspan="7" class="empty">No pages in this range.</td></tr>
        </tbody>
      </table>
    </div>

    <div v-show="table === 'queries'" class="sc-table-wrap">
      <table class="sc-table">
        <thead>
          <tr>
            <th>Query</th>
            <th class="num">Clicks</th>
            <th class="num">Impressions</th>
            <th class="num">CTR</th>
            <th class="num">Avg pos</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in queries" :key="row.query">
            <td>{{ row.query }}</td>
            <td class="num">{{ fmtNum(row.clicks) }}</td>
            <td class="num">{{ fmtNum(row.impressions) }}</td>
            <td class="num">{{ fmtPct(row.ctr) }}</td>
            <td class="num">{{ fmtPos(row.position) }}</td>
          </tr>
          <tr v-if="!queries.length"><td colspan="5" class="empty">No queries in this range.</td></tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../config'

// Validated against the admin dark surface (#0e121b) with the dataviz
// validator: lightness band, chroma floor, all-pairs CVD ΔE 9.4, normal-vision
// ΔE 20.9, contrast >= 3:1. The UI accent (#1fb2e8) is deliberately NOT used for
// data — it sits outside the dark lightness band.
const SERIES_BLUE = '#3987e5'
const SERIES_ORANGE = '#d95926'
const SERIES_AQUA = '#199e70'
// Status palette is fixed and never reused for a series.
const STATUS_GOOD = '#0ca30c'
const STATUS_CRITICAL = '#d03b3b'
const STATUS_WARNING = '#fab219'

export default {
  name: 'SearchConsole',
  data () {
    return {
      status: null,
      daily: [],
      devices: [],
      totals: null,
      pages: [],
      queries: [],
      parts: [],
      diagnostics: null,
      diagnosticScope: 'parts',
      genAi: null,
      coverage: [],
      coverageByType: [],
      catalogParts: 0,
      days: 28,
      partType: '',
      host: '',
      hosts: [],
      table: 'parts',
      loading: false,
      syncing: false,
      importingGenAi: false,
      syncMessage: '',
      genAiMessage: '',
      error: null,
      hover: null,
      plot: { width: 720, height: 170, padLeft: 8, padRight: 8, padTop: 14, padBottom: 14 },
      rangeOptions: [
        { days: 7, label: '7 days' },
        { days: 28, label: '28 days' },
        { days: 90, label: '3 months' },
        { days: 180, label: '6 months' },
        { days: 480, label: '16 months' }
      ],
      partTypes: ['cpu', 'motherboard', 'ram', 'storage', 'gpu', 'psu', 'case']
    }
  },
  computed: {
    kpiCards () {
      const t = this.totals || {}
      return [
        { key: 'clicks', label: 'Clicks', value: this.fmtNum(t.clicks), note: `over ${this.daily.length} days` },
        { key: 'impressions', label: 'Impressions', value: this.fmtNum(t.impressions), note: 'times a result was seen' },
        { key: 'ctr', label: 'Average CTR', value: this.fmtPct(t.ctr), note: 'clicks / impressions' },
        { key: 'position', label: 'Average position', value: this.fmtPos(t.position), note: 'impression-weighted' },
        {
          key: 'indexed',
          label: 'Indexed parts',
          value: this.indexSummary.checked ? this.fmtNum(this.indexSummary.indexed) : '—',
          // A partial scan must say so — "of 40 checked" alone reads as full
          // coverage when the catalog is 444.
          note: !this.indexSummary.checked
            ? 'not checked yet'
            : (this.indexSummary.partial
              ? `of ${this.fmtNum(this.indexSummary.checked)} checked · ${this.fmtNum(this.indexSummary.catalog)} in catalog`
              : `of ${this.fmtNum(this.indexSummary.catalog)} part pages`)
        }
      ]
    },
    // One chart per measure — never a shared y-axis across different scales.
    trendCharts () {
      return [
        this._buildChart('clicks', 'Clicks', 'Visits from Google search results', SERIES_BLUE, this.fmtNum),
        this._buildChart('impressions', 'Impressions', 'Times a result appeared', SERIES_ORANGE, this.fmtNum),
        this._buildChart('position', 'Average position', 'Lower is better — axis inverted', SERIES_AQUA, this.fmtPos, true)
      ]
    },
    deviceRows () {
      return this.devices
        .slice()
        .sort((a, b) => Number(b.impressions || 0) - Number(a.impressions || 0))
        .map((row) => ({
          device: row.device,
          label: this.prettyDevice(row.device),
          clicks: Number(row.clicks || 0),
          impressions: Number(row.impressions || 0),
          ctr: Number(row.ctr || 0),
          position: Number(row.position || 0)
        }))
    },
    diagnosticScopeData () {
      return this.diagnostics && this.diagnostics.scopes
        ? this.diagnostics.scopes[this.diagnosticScope]
        : null
    },
    positionBuckets () {
      return this.diagnosticScopeData && Array.isArray(this.diagnosticScopeData.buckets)
        ? this.diagnosticScopeData.buckets
        : []
    },
    rankingHeadline () {
      const scope = this.diagnosticScopeData
      if (!scope || !scope.impressions) return 'Not enough data yet.'
      return `${this.fmtPct(scope.below_top_ten_share)} of impressions rank below the top 10.`
    },
    rankingSummary () {
      const scope = this.diagnosticScopeData
      if (!scope || !scope.impressions) return 'Waiting for enough mirrored page data to diagnose rankings.'
      const name = this.diagnosticScope === 'parts' ? 'part-page' : 'page'
      return `${this.fmtPct(scope.below_top_ten_share)} of ${name} impressions are below position 10. Compare CTR inside each bucket instead of judging the blended site average.`
    },
    nearWins () {
      return this.diagnostics && Array.isArray(this.diagnostics.near_wins) ? this.diagnostics.near_wins : []
    },
    genAiBars () {
      const rows = this.genAi && Array.isArray(this.genAi.daily) ? this.genAi.daily : []
      const max = rows.reduce((highest, row) => Math.max(highest, Number(row.impressions || 0)), 0) || 1
      return rows.map((row) => ({
        date: row.date,
        impressions: Number(row.impressions || 0),
        height: Number(row.impressions || 0) ? Math.max(4, Math.round((Number(row.impressions || 0) / max) * 100)) : 1
      }))
    },
    genAiTopPages () {
      return this.genAi && Array.isArray(this.genAi.pages) ? this.genAi.pages.slice(0, 10) : []
    },
    genAiFirstDate () {
      return this.genAiBars.length ? this.genAiBars[0].date : ''
    },
    genAiLastDate () {
      return this.genAiBars.length ? this.genAiBars[this.genAiBars.length - 1].date : ''
    },
    genAiSnapshotLabel () {
      const snapshot = this.genAi && this.genAi.snapshot
      return snapshot ? `${snapshot.range_start} – ${snapshot.range_end} export` : 'latest imported export'
    },
    coverageChart () {
      const total = this.coverage.reduce((sum, row) => sum + Number(row.total || 0), 0) || 1
      return this.coverage.slice(0, 5).map((row) => ({
        label: row.coverage_state || row.verdict || 'Unknown',
        count: Number(row.total || 0),
        percent: Math.max(3, Math.round((Number(row.total || 0) / total) * 100)),
        color: this.verdictColor(row.verdict)
      }))
    },
    indexSummary () {
      const checked = this.coverageByType.reduce((sum, row) => sum + Number(row.total || 0), 0)
      const indexed = this.coverageByType.reduce((sum, row) => sum + Number(row.indexed || 0), 0)
      // `catalog` is every part URL that could be inspected. `checked` is how
      // many we've actually spent URL Inspection quota on — the two only match
      // once the index-check job has walked the whole catalog.
      const catalog = this.catalogParts || checked
      return {
        checked,
        catalog,
        indexed,
        notIndexed: Math.max(0, checked - indexed),
        indexedPercent: checked ? Math.round((indexed / checked) * 100) : 0,
        scanPercent: catalog ? Math.round((checked / catalog) * 100) : 0,
        partial: checked > 0 && checked < catalog
      }
    },
    hoverRow () {
      if (!this.hover) return null
      return this.daily[this.hover.index] || null
    },
    tooltipStyle () {
      if (!this.hover) return {}
      // Flip the tooltip to the left near the right edge so it can't overflow.
      const percent = this.hover.ratio * 100
      return percent > 65
        ? { right: (100 - percent) + '%', marginRight: '10px' }
        : { left: percent + '%', marginLeft: '10px' }
    },
    authModeLabel () {
      const mode = this.status && this.status.auth_mode
      return mode === 'oauth' ? 'OAuth' : (mode === 'service_account' ? 'service account' : '')
    },
    firstDate () { return this.daily.length ? this.daily[0].date : '' },
    lastDate () { return this.daily.length ? this.daily[this.daily.length - 1].date : '' }
  },
  mounted () {
    this.fetchAll()
  },
  methods: {
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    async fetchAll () {
      this.loading = true
      this.error = null
      try {
        await Promise.all([
          this.fetchStatus(),
          this.fetchOverview(),
          this.fetchPages(),
          this.fetchQueries(),
          this.fetchParts(),
          this.fetchCoverage(),
          this.fetchDiagnostics(),
          this.fetchGenAi()
        ])
      } finally {
        this.loading = false
      }
    },
    async fetchStatus () {
      try {
        const { data } = await axios.get(`${apiDomain}admin/search-console/status`, { headers: this.authHeaders() })
        this.status = data
      } catch (e) { this.status = null }
    },
    async fetchOverview () {
      try {
        const { data } = await axios.get(
          `${apiDomain}admin/search-console/overview?days=${this.days}`,
          { headers: this.authHeaders() }
        )
        this.daily = Array.isArray(data.daily) ? data.daily : []
        this.devices = Array.isArray(data.devices) ? data.devices : []
        this.totals = data.totals || null
      } catch (e) {
        this.error = this.errMsg(e)
      }
    },
    async fetchPages () {
      try {
        const { data } = await axios.get(
          `${apiDomain}admin/search-console/pages?days=${this.days}&limit=100${this.hostParam()}`,
          { headers: this.authHeaders() }
        )
        this.pages = Array.isArray(data.pages) ? data.pages : []
        if (Array.isArray(data.hosts)) this.hosts = data.hosts
      } catch (e) { this.pages = [] }
    },
    hostParam () {
      return this.host ? `&host=${encodeURIComponent(this.host)}` : ''
    },
    onHostChange () {
      this.fetchPages()
      this.fetchParts()
      this.fetchDiagnostics()
    },
    async fetchQueries () {
      try {
        const { data } = await axios.get(
          `${apiDomain}admin/search-console/queries?days=${this.days}&limit=100`,
          { headers: this.authHeaders() }
        )
        this.queries = Array.isArray(data.queries) ? data.queries : []
      } catch (e) { this.queries = [] }
    },
    async fetchParts () {
      try {
        const type = this.partType ? `&partType=${encodeURIComponent(this.partType)}` : ''
        const { data } = await axios.get(
          `${apiDomain}admin/search-console/parts?days=${this.days}&limit=100${type}${this.hostParam()}`,
          { headers: this.authHeaders() }
        )
        this.parts = Array.isArray(data.parts) ? data.parts : []
        if (Array.isArray(data.hosts)) this.hosts = data.hosts
      } catch (e) { this.parts = [] }
    },
    async fetchCoverage () {
      try {
        const { data } = await axios.get(`${apiDomain}admin/search-console/index-coverage`, { headers: this.authHeaders() })
        this.coverage = Array.isArray(data.coverage) ? data.coverage : []
        this.coverageByType = Array.isArray(data.by_type) ? data.by_type : []
        this.catalogParts = Number(data.catalog_parts || 0)
      } catch (e) {
        this.coverage = []
        this.coverageByType = []
        this.catalogParts = 0
      }
    },
    async fetchDiagnostics () {
      try {
        const { data } = await axios.get(
          `${apiDomain}admin/search-console/diagnostics?days=${this.days}${this.hostParam()}`,
          { headers: this.authHeaders() }
        )
        this.diagnostics = data || null
      } catch (e) {
        this.diagnostics = null
      }
    },
    async fetchGenAi () {
      try {
        const { data } = await axios.get(
          `${apiDomain}admin/search-console/generative-ai?days=${this.days}`,
          { headers: this.authHeaders() }
        )
        this.genAi = data || null
      } catch (e) {
        this.genAi = null
      }
    },
    openGenAiImport () {
      if (this.$refs.genAiInput) this.$refs.genAiInput.click()
    },
    async importGenAiReport (event) {
      const selected = Array.from((event && event.target && event.target.files) || [])
      if (!selected.length) return

      this.importingGenAi = true
      this.genAiMessage = ''
      try {
        // Directory selection includes the nested "full report" export too.
        // Only files immediately inside the outer folder are the AI report.
        const roots = selected.filter((file) => {
          const relative = String(file.webkitRelativePath || '')
          return !relative || relative.split('/').length === 2
        })
        const byName = new Map(roots.map((file) => [String(file.name || '').toLowerCase(), file]))
        const names = {
          chart: 'chart.csv',
          pages: 'pages.csv',
          countries: 'countries.csv',
          devices: 'devices.csv',
          filters: 'filters.csv'
        }
        const required = ['chart', 'pages', 'devices']
        const missing = required.filter((key) => !byName.has(names[key]))
        if (missing.length) throw new Error(`Missing ${missing.map((key) => names[key]).join(', ')} in the selected outer folder.`)

        const files = {}
        await Promise.all(Object.keys(names).map(async (key) => {
          const file = byName.get(names[key])
          files[key] = file ? await this.readTextFile(file) : ''
        }))
        const { data } = await axios.post(
          `${apiDomain}admin/search-console/generative-ai/import`,
          { files },
          { headers: this.authHeaders() }
        )
        const stats = data.stats || {}
        this.genAiMessage = `Imported ${this.fmtNum(stats.totalImpressions)} AI impressions across ${this.fmtNum(stats.pageRows)} pages (${stats.rangeStart} – ${stats.rangeEnd}).`
        await this.fetchGenAi()
      } catch (e) {
        this.genAiMessage = this.errMsg(e)
      } finally {
        this.importingGenAi = false
        if (event && event.target) event.target.value = ''
      }
    },
    readTextFile (file) {
      if (file && typeof file.text === 'function') return file.text()
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => reject(reader.error || new Error('Could not read CSV file'))
        reader.readAsText(file)
      })
    },
    async syncNow () {
      this.syncing = true
      this.syncMessage = ''
      try {
        const { data } = await axios.post(
          `${apiDomain}admin/search-console/sync`,
          { days: Math.min(this.days, 90) },
          { headers: this.authHeaders() }
        )
        const s = data.stats || {}
        this.syncMessage = `Synced ${s.startDate} to ${s.endDate}: ${this.fmtNum(s.clicks)} clicks, ` +
          `${this.fmtNum(s.impressions)} impressions, ${this.fmtNum(s.partPageRows)} part-page rows.`
        await this.fetchAll()
      } catch (e) {
        this.syncMessage = ''
        this.error = this.errMsg(e)
      } finally {
        this.syncing = false
      }
    },
    setRange (days) {
      if (this.days === days) return
      this.days = days
      this.hover = null
      this.fetchAll()
    },

    // ---- chart geometry ----
    /**
     * Build one single-series line chart. `invert` flips the y-scale for average
     * position, where a lower number is better.
     */
    _buildChart (field, title, subtitle, color, format, invert) {
      const rows = this.daily
      const p = this.plot
      const innerW = p.width - p.padLeft - p.padRight
      const innerH = p.height - p.padTop - p.padBottom

      const values = rows.map((row) => Number(row[field] || 0))
      const total = field === 'position'
        ? (this.totals ? Number(this.totals.position || 0) : 0)
        : values.reduce((sum, v) => sum + v, 0)

      const base = {
        key: field,
        field,
        title,
        subtitle,
        color,
        format,
        totalLabel: format(total),
        line: '',
        area: '',
        points: [],
        gridY: [],
        yLabels: []
      }
      if (!rows.length) return base

      let min = Math.min(...values)
      let max = Math.max(...values)
      if (field === 'position') {
        // Position charts read better with a little headroom than forced to 0.
        min = Math.max(1, Math.floor(min - 1))
        max = Math.ceil(max + 1)
      } else {
        min = 0
        max = Math.max(1, max)
      }
      if (max === min) max = min + 1

      const x = (i) => p.padLeft + (rows.length === 1 ? innerW / 2 : (i / (rows.length - 1)) * innerW)
      const y = (v) => {
        const ratio = (v - min) / (max - min)
        // invert: smaller value plots higher
        return invert ? p.padTop + ratio * innerH : p.padTop + (1 - ratio) * innerH
      }

      const points = values.map((v, i) => ({ x: x(i), y: y(v), value: v }))
      const line = points.map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt.x.toFixed(1)},${pt.y.toFixed(1)}`).join(' ')
      // Only fill toward a zero baseline. On the inverted position axis the
      // baseline is the TOP of the plot, so an area would shade the whole upper
      // block — visually heavy and meaningless. Position gets a bare line.
      const baseline = p.height - p.padBottom
      const area = invert
        ? ''
        : `${line} L${points[points.length - 1].x.toFixed(1)},${baseline} L${points[0].x.toFixed(1)},${baseline} Z`

      const gridY = [0, 0.5, 1].map((r) => p.padTop + r * innerH)
      const yLabels = invert
        ? [format(min), format((min + max) / 2), format(max)]
        : [format(max), format((min + max) / 2), format(min)]

      return { ...base, line, area, points, gridY, yLabels }
    },
    onPlotMove (event) {
      if (!this.daily.length) return
      const rect = event.currentTarget.getBoundingClientRect()
      if (!rect.width) return
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
      const index = Math.round(ratio * (this.daily.length - 1))
      this.hover = { index, ratio }
    },
    hoverPoint (chart) {
      if (!this.hover || !chart.points.length) return null
      return chart.points[this.hover.index] || null
    },

    // ---- formatting ----
    fmtNum (value) {
      const n = Number(value || 0)
      if (!Number.isFinite(n)) return '0'
      if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
      if (n >= 10000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
      return n.toLocaleString()
    },
    fmtPct (value) {
      const n = Number(value || 0)
      return Number.isFinite(n) ? (n * 100).toFixed(1) + '%' : '0%'
    },
    fmtPos (value) {
      const n = Number(value || 0)
      return Number.isFinite(n) && n > 0 ? n.toFixed(1) : '—'
    },
    prettyDevice (device) {
      const map = { DESKTOP: 'Desktop', MOBILE: 'Mobile', TABLET: 'Tablet' }
      const key = String(device || '').toUpperCase()
      return map[key] || (device || 'Unknown')
    },
    prettyType (value) {
      const text = String(value || 'other').replace(/[-_]/g, ' ')
      return text.charAt(0).toUpperCase() + text.slice(1)
    },
    opportunityLabel (part) {
      return Number(part.position || 0) <= 10 ? 'Top 10 · improve CTR' : 'Close to top 10'
    },
    verdictColor (verdict) {
      const v = String(verdict || '').toUpperCase()
      if (v === 'PASS') return STATUS_GOOD
      if (v === 'PARTIAL' || v === 'NEUTRAL') return STATUS_WARNING
      return STATUS_CRITICAL
    },
    partUrl (part) {
      if (part.page) return part.page
      const origin = (this.status && this.status.site_origin) || ''
      return `${origin}${part.page_path || ''}`
    },
    errMsg (e) {
      if (e && e.response && e.response.data && e.response.data.message) return e.response.data.message
      return (e && e.message) || 'Request failed'
    }
  }
}
</script>

<style scoped>
.sc-page {
  padding-bottom: 24px;
}

.eyebrow {
  display: block;
  margin-bottom: 3px;
  color: #1997c6;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.sc-setup {
  margin: 16px 0;
  padding: 16px 18px;
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 8px;
  background: rgba(14, 18, 27, 0.78);
}

.sc-setup h5 {
  margin: 0 0 6px;
  color: #eaf0f7;
  font-size: 15px;
  font-weight: 800;
}

.sc-setup p {
  margin: 0;
  max-width: 720px;
  color: #9aa3b4;
  font-size: 12.5px;
  line-height: 1.6;
}

.sc-setup code {
  padding: 1px 5px;
  border-radius: 4px;
  background: rgba(58, 63, 77, 0.6);
  color: #cfd6e2;
  font-size: 11.5px;
}

.alert {
  margin: 12px 0;
  padding: 10px 12px;
  border: 1px solid rgba(232, 79, 106, 0.5);
  border-radius: 8px;
  background: rgba(90, 34, 48, 0.48);
  color: #f3c6cf;
  font-size: 13px;
}

/* Filters sit in one row above the charts. */
.sc-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin: 16px 0 4px;
}

.sc-ranges {
  display: flex;
  gap: 4px;
}

.sc-range {
  padding: 7px 12px;
  border: 1px solid rgba(67, 72, 87, 0.8);
  border-radius: 6px;
  background: transparent;
  color: #9aa3b4;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
}

.sc-range:hover { color: #d7dce8; }

.sc-range.active {
  border-color: rgba(31, 178, 232, 0.6);
  background: rgba(31, 178, 232, 0.12);
  color: #1fb2e8;
}

.sc-control-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sc-freshness {
  color: #8f98aa;
  font-size: 11.5px;
}

.sc-freshness strong { color: #cfd6e2; }

.btn-refresh {
  padding: 7px 13px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 6px;
  background: rgba(20, 25, 36, 0.9);
  color: #cfd6e2;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
}

.btn-refresh:disabled { opacity: 0.55; cursor: default; }

.sc-sync-note {
  margin: 6px 0 0;
  color: #8fd3a8;
  font-size: 12px;
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
  margin: 18px 0 8px;
}

.kpi-card {
  display: grid;
  gap: 6px;
  padding: 16px;
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 8px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.08), transparent 38%),
    rgba(14, 18, 27, 0.78);
}

.kpi-label {
  color: #8f98aa;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

/* Proportional figures for standalone values; tabular is for aligned columns. */
.kpi-value {
  color: #eaf0f7;
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
}

.kpi-note {
  overflow: hidden;
  color: #8f98aa;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Small multiples: one row per measure, shared x-range. */
.sc-charts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin: 14px 0;
}

.sc-chart-card {
  min-width: 0;
  margin: 0;
  padding: 14px;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background: rgba(9, 13, 20, 0.68);
}

.sc-chart-card figcaption {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.sc-chart-title {
  display: block;
  color: #dce1eb;
  font-size: 12px;
  font-weight: 800;
}

.sc-chart-sub {
  display: block;
  margin-top: 2px;
  color: #8f98aa;
  font-size: 10.5px;
}

.sc-chart-total {
  font-size: 15px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.sc-plot {
  position: relative;
}

.sc-svg {
  display: block;
  width: 100%;
  height: 170px;
  overflow: visible;
}

.sc-grid {
  stroke: #2c303a;
  stroke-width: 1;
  vector-effect: non-scaling-stroke;
}

.sc-line {
  stroke-width: 2;
  stroke-linejoin: round;
  stroke-linecap: round;
  vector-effect: non-scaling-stroke;
}

.sc-area {
  opacity: 0.14;
}

.sc-crosshair {
  stroke: #6f7889;
  stroke-width: 1;
  stroke-dasharray: 3 3;
  vector-effect: non-scaling-stroke;
}

/* 2px surface ring keeps the marker readable over the line. */
.sc-dot {
  stroke: #090d14;
  stroke-width: 2;
  vector-effect: non-scaling-stroke;
}

.sc-hit {
  fill: transparent;
  cursor: crosshair;
}

.sc-axis-y {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 170px;
  padding: 8px 0;
  color: #898781;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
}

.sc-axis-x {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  color: #898781;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
}

.sc-tooltip {
  position: absolute;
  top: 6px;
  z-index: 5;
  display: grid;
  gap: 3px;
  padding: 7px 9px;
  border: 1px solid rgba(80, 87, 104, 0.9);
  border-radius: 6px;
  background: rgba(10, 14, 21, 0.97);
  pointer-events: none;
  white-space: nowrap;
}

.sc-tip-date {
  color: #8f98aa;
  font-size: 10.5px;
  font-variant-numeric: tabular-nums;
}

.sc-tip-value {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #cfd6e2;
  font-size: 11.5px;
}

.sc-tip-value i {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

.sc-tip-value strong {
  color: #eaf0f7;
  font-variant-numeric: tabular-nums;
}

.audience-insights {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin: 14px 0;
}

.insight-card {
  min-width: 0;
  padding: 13px 14px;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background:
    linear-gradient(135deg, rgba(25, 151, 198, 0.07), transparent 42%),
    rgba(9, 13, 20, 0.68);
}

.insight-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 11px;
}

.insight-head span {
  color: #8f98aa;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.insight-head strong {
  overflow: hidden;
  color: #dce1eb;
  font-size: 12px;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.insight-bars {
  display: grid;
  gap: 8px;
}

.insight-bar {
  display: grid;
  grid-template-columns: minmax(70px, 0.8fr) minmax(90px, 1fr) 38px;
  align-items: center;
  gap: 8px;
  color: #aeb5c5;
  font-size: 11.5px;
}

.insight-bar > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.insight-bar > div {
  height: 7px;
  overflow: hidden;
  border-radius: 4px;
  background: rgba(52, 58, 72, 0.7);
}

.insight-bar > div i {
  display: block;
  height: 100%;
  border-radius: 4px;
}

.insight-bar > strong {
  color: #dce1eb;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.sc-coverage {
  display: grid;
  gap: 8px;
}

.sc-coverage-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #aeb5c5;
  font-size: 11.5px;
}

.sc-coverage-row strong {
  color: #dce1eb;
  font-variant-numeric: tabular-nums;
}

/* Status is icon + label, never color alone. */
.sc-status {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.sc-status i {
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.sc-status i.ok { background: #0ca30c; }
.sc-status i.bad { background: #d03b3b; }

.sc-meter {
  height: 7px;
  overflow: hidden;
  border-radius: 4px;
  background: rgba(52, 58, 72, 0.7);
}

.sc-meter i {
  display: block;
  height: 100%;
  border-radius: 4px;
  background: #0ca30c;
}

.sc-coverage-note {
  color: #8f98aa;
  font-size: 10.5px;
}

.sc-analysis-section,
.sc-ai-section,
.sc-opportunity-section {
  margin: 22px 0;
}

.sc-section-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 12px;
}

.sc-section-head h5 {
  margin: 0;
  color: #eaf0f7;
  font-size: 17px;
  font-weight: 800;
}

.sc-section-head p {
  max-width: 760px;
  margin: 5px 0 0;
  color: #8f98aa;
  font-size: 11.5px;
  line-height: 1.5;
}

.sc-segmented {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 3px;
  padding: 3px;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 7px;
  background: rgba(9, 13, 20, 0.76);
}

.sc-segmented button {
  padding: 6px 10px;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: #8f98aa;
  cursor: pointer;
  font-size: 11px;
  font-weight: 800;
}

.sc-segmented button.active {
  background: rgba(31, 178, 232, 0.14);
  color: #43bce8;
}

.sc-diagnostic-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.sc-diagnostic-card {
  min-width: 0;
  padding: 14px;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background: rgba(9, 13, 20, 0.68);
}

.sc-rank-head,
.sc-rank-row,
.sc-device-head,
.sc-device-row {
  display: grid;
  grid-template-columns: 1.1fr repeat(4, minmax(48px, 0.7fr));
  align-items: center;
  gap: 8px;
}

.sc-rank-head,
.sc-device-head {
  padding: 0 7px 7px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.72);
  color: #7f899c;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.sc-rank-row,
.sc-device-row {
  padding: 8px 7px;
  border-bottom: 1px solid rgba(46, 51, 64, 0.55);
  color: #b9c0ce;
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
}

.sc-rank-row:last-child,
.sc-device-row:last-child { border-bottom: 0; }
.sc-rank-row strong,
.sc-device-row strong { color: #dce1eb; }
.sc-rank-head span:not(:first-child),
.sc-rank-row span,
.sc-device-head span:not(:first-child),
.sc-device-row span { text-align: right; }

.sc-method-note {
  margin: 10px 0 0;
  color: #737d90;
  font-size: 10px;
  line-height: 1.45;
}

.sc-interpretation {
  display: grid;
  gap: 7px;
}

.sc-interpretation strong {
  color: #dce1eb;
  font-size: 16px;
}

.sc-interpretation span {
  color: #8f98aa;
  font-size: 11px;
  line-height: 1.5;
}

.sc-import-actions { flex: 0 0 auto; }
.sc-file-input { display: none; }

.sc-ai-section {
  padding: 16px;
  border: 1px solid rgba(25, 151, 198, 0.42);
  border-radius: 9px;
  background:
    radial-gradient(circle at 8% 0%, rgba(25, 151, 198, 0.11), transparent 32%),
    rgba(9, 13, 20, 0.72);
}

.sc-ai-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin: 14px 0;
}

.sc-ai-kpi {
  display: grid;
  gap: 4px;
  padding: 12px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 7px;
  background: rgba(14, 18, 27, 0.72);
}

.sc-ai-kpi span {
  color: #8f98aa;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.sc-ai-kpi strong {
  color: #eaf0f7;
  font-size: 22px;
  font-variant-numeric: tabular-nums;
}

.sc-ai-kpi small {
  overflow: hidden;
  color: #737d90;
  font-size: 9.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sc-ai-trend {
  margin: 12px 0 14px;
  padding: 12px 12px 8px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 7px;
  background: rgba(6, 10, 16, 0.58);
}

.sc-ai-trend-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #8f98aa;
  font-size: 10.5px;
}

.sc-ai-trend-head strong { color: #cfd6e2; }

.sc-ai-bars {
  display: flex;
  align-items: flex-end;
  height: 78px;
  gap: 3px;
}

.sc-ai-bars i {
  flex: 1 1 0;
  min-width: 2px;
  border-radius: 2px 2px 0 0;
  background: #1997c6;
  opacity: 0.88;
}

.sc-ai-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(240px, 0.7fr);
  gap: 12px;
  align-items: start;
}

.sc-ai-breakdowns {
  display: grid;
  gap: 10px;
}

.sc-breakdown-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(46, 51, 64, 0.55);
  color: #aeb5c5;
  font-size: 11px;
}

.sc-breakdown-row:last-child { border-bottom: 0; }
.sc-breakdown-row strong { color: #dce1eb; font-variant-numeric: tabular-nums; }
.sc-ai-empty { margin-bottom: 0; }

.sc-list-empty {
  color: #7c8496;
  font-size: 11.5px;
}

.tabs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin: 22px 0 12px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.8);
}

.tabs {
  display: flex;
  gap: 4px;
}

.tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 14px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #9aa3b4;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
}

.tab:hover { color: #d7dce8; }

.tab.active {
  color: #1fb2e8;
  border-bottom-color: #1fb2e8;
}

.tab-count {
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(58, 63, 77, 0.6);
  color: #aeb5c5;
  font-size: 10.5px;
  font-weight: 800;
}

.hfilter {
  padding: 7px 10px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 6px;
  background: rgba(20, 25, 36, 0.9);
  color: #cfd6e2;
  font-size: 12px;
}

.sc-table-wrap {
  overflow-x: auto;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background: rgba(9, 13, 20, 0.68);
}

.sc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.sc-table th {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.8);
  color: #8f98aa;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.sc-table td {
  padding: 9px 12px;
  border-bottom: 1px solid rgba(46, 51, 64, 0.6);
  color: #cfd6e2;
}

.sc-table tbody tr:last-child td { border-bottom: 0; }

.sc-table tbody tr:hover td { background: rgba(31, 178, 232, 0.05); }

/* Tabular figures so the numeric columns align vertically. */
.sc-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.sc-table .muted { color: #8f98aa; }

.sc-table a {
  color: #cfd6e2;
  text-decoration: none;
}

.sc-table a:hover {
  color: #1fb2e8;
  text-decoration: underline;
}

.sc-table td.empty {
  padding: 22px 12px;
  color: #7c8496;
  text-align: center;
}

.sc-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 800;
  white-space: nowrap;
}

.sc-pill.ok { background: rgba(12, 163, 12, 0.16); color: #57c96a; }
.sc-pill.bad { background: rgba(208, 59, 59, 0.16); color: #e8737a; }
.sc-pill.unknown { background: rgba(90, 97, 114, 0.22); color: #99a1b2; }
.sc-pill.opportunity { background: rgba(250, 178, 25, 0.14); color: #f0c75e; }

.sc-empty {
  margin: 10px 0;
  padding: 18px;
  border: 1px dashed rgba(67, 72, 87, 0.8);
  border-radius: 8px;
  color: #7c8496;
  font-size: 12.5px;
  text-align: center;
}

@media (max-width: 1200px) {
  .sc-charts { grid-template-columns: 1fr; }
  .audience-insights { grid-template-columns: 1fr; }
  .kpi-row { grid-template-columns: repeat(2, 1fr); }
  .sc-diagnostic-grid,
  .sc-ai-grid { grid-template-columns: 1fr; }
  .sc-ai-kpis { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 720px) {
  .sc-section-head { align-items: flex-start; flex-direction: column; }
  .sc-ai-kpis { grid-template-columns: 1fr; }
  .sc-rank-head,
  .sc-rank-row,
  .sc-device-head,
  .sc-device-row { grid-template-columns: 1fr repeat(4, minmax(42px, 0.7fr)); gap: 5px; }
}
</style>
